import { useEffect, useMemo, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders } from "../profileUtils";
import { TabSpinner, TabError, TabEmpty } from "./FamilyTab";
import s from "./GradesTab.module.css";
import { syLabel } from "../../../../utils/schoolYear";
import {
  averageOf, fetchGradingPeriods, periodsForOffering, withLegacyPeriods,
} from "../../../../utils/gradingPeriods";

export default function GradesTab({ studentId, schoolYears = [], semesters = [] }) {
  const [grades,       setGrades]       = useState([]);
  const [offerings,    setOfferings]    = useState([]);
  // Lookups for subject names and each class's grading periods.
  const [subjects,     setSubjects]     = useState([]);
  const [sections,     setSections]     = useState([]);
  const [gradeLevels,  setGradeLevels]  = useState([]);
  const [periodRows,   setPeriodRows]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError("");

    Promise.all([
      fetch(`${API_BASE}/api/grades?studentId=${studentId}`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/class-offerings`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : []),
      fetch(`${API_BASE}/api/subjects`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/sections`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_BASE}/api/grade-levels`).then(r => r.ok ? r.json() : []).catch(() => []),
      fetchGradingPeriods(),
    ])
      .then(([gradeData, offeringData, subj, sec, gl, pr]) => {
        if (cancelled) return;
        const arr = x => (Array.isArray(x) ? x : []);
        setGrades(arr(gradeData));
        setOfferings(arr(offeringData));
        setSubjects(arr(subj)); setSections(arr(sec)); setGradeLevels(arr(gl)); setPeriodRows(arr(pr));
      })
      .catch(() => { if (!cancelled) setError("Could not load grade records."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [studentId]);

  const syById  = useMemo(() => Object.fromEntries(schoolYears.map(y => [y.id, y])), [schoolYears]);
  const semById = useMemo(() => Object.fromEntries(semesters.map(m => [m.id, m])),   [semesters]);

  /* Build offering map: id → { subjectTitle, semesterId, schoolYearId } */
  const offeringById = useMemo(() => {
    const map = {};
    offerings.forEach(o => { map[o.id] = o; });
    return map;
  }, [offerings]);

  const subjectById = useMemo(() => Object.fromEntries(subjects.map(x => [x.id, x])), [subjects]);

  /* Group grades: schoolYearId → semesterId → subjectTitle → { offering, [periodCode]: grade } */
  const grouped = useMemo(() => {
    const byYear = new Map();
    grades.forEach(g => {
      const offering = offeringById[g.classOfferingId];
      if (!offering) return;
      // Offerings carry a semester only — the school year comes from it.
      const semId   = offering.semesterId ?? "unknown";
      const syId    = semById[semId]?.schoolYearId ?? "unknown";
      const subject = subjectById[offering.subjectId]?.title || `Subject #${offering.subjectId}`;

      if (!byYear.has(syId)) byYear.set(syId, new Map());
      const bySem = byYear.get(syId);
      if (!bySem.has(semId)) bySem.set(semId, new Map());
      const bySubject = bySem.get(semId);
      if (!bySubject.has(subject)) bySubject.set(subject, { offering });
      bySubject.get(subject)[g.period] = g;
    });
    /* Sort years newest first */
    return [...byYear.entries()].sort(([a], [b]) => {
      const ya = syById[a]?.label || "";
      const yb = syById[b]?.label || "";
      return yb.localeCompare(ya);
    });
  }, [grades, offeringById, syById, semById, subjectById]);

  /** A class's grading periods (quarters, midterm/final…) + any older codes
   *  that already hold grades. */
  function periodsFor(cell) {
    return withLegacyPeriods(
      periodsForOffering({ offering: cell.offering, sections, gradeLevels, semesters, rows: periodRows }),
      Object.values(cell).filter(v => v && v.period)
    );
  }

  /* Summary counts */
  const totalSubjects = useMemo(() => {
    const seen = new Set();
    grades.forEach(g => {
      const o = offeringById[g.classOfferingId];
      if (o) seen.add(`${o.semesterId}-${o.classOfferingId || g.classOfferingId}`);
    });
    return seen.size;
  }, [grades, offeringById]);

  if (loading) return <TabSpinner label="Loading grade records…" />;
  if (error)   return <TabError message={error} />;
  if (grades.length === 0) return (
    <TabEmpty
      icon="📊"
      title="No grades recorded"
      sub="Grade entries made by teachers will appear here once encoded."
    />
  );

  return (
    <div className={s.root}>
      <div className={s.summary}>
        <span className={s.summaryNum}>{grades.length}</span> grade entries across
        <span className={s.summaryNum}> {totalSubjects}</span> subject{totalSubjects !== 1 ? "s" : ""}
      </div>

      {grouped.map(([syId, bySem], gIdx) => {
        const sy = syById[syId];
        return (
          <div key={syId} className={s.yearBlock} style={{ animationDelay: `${gIdx * 60}ms` }}>
            <div className={s.yearHead}>
              <span className={s.yearDot} aria-hidden="true" />
              <span className={s.yearLabel}>{sy ? syLabel(sy.label) : "Unknown Year"}</span>
            </div>

            {[...bySem.entries()].map(([semId, bySubject]) => {
              const sem = semById[semId];
              const subjects = [...bySubject.entries()];
              // Table columns = every period used by this term's subjects.
              const cols = [];
              for (const [, cell] of subjects) {
                for (const pp of periodsFor(cell)) {
                  if (!cols.some(c => c.code === pp.code)) cols.push(pp);
                }
              }
              return (
                <div key={semId} className={s.semBlock}>
                  {sem && (
                    <div className={s.semLabel}>{sem.label}</div>
                  )}
                  <div className={s.tableWrap}>
                    <table className={s.table}>
                      <thead>
                        <tr>
                          <th className={s.thSubject}>Subject</th>
                          {cols.map(c => <th key={c.code} className={s.thScore}>{c.label}</th>)}
                          <th className={s.thRemarks}>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subjects.map(([subjectTitle, cell]) => {
                          // Passed/Failed once every one of the class's own
                          // periods has a score; Pending until then.
                          const own = periodsFor(cell).filter(pp => !pp.legacy).map(pp => pp.code);
                          const complete = own.length > 0 && own.every(c => cell[c]?.score != null);
                          const avg  = complete ? averageOf(cell, own) : null;
                          const pass = avg != null ? avg >= 75 : null;
                          return (
                            <tr key={subjectTitle} className={s.gradeRow}>
                              <td className={s.tdSubject}>{subjectTitle}</td>
                              {cols.map(c => (
                                <td key={c.code} className={s.tdScore}>
                                  <ScoreChip score={cell[c.code]?.score} />
                                </td>
                              ))}
                              <td className={s.tdRemarks}>
                                {pass != null
                                  ? <span className={pass ? s.pass : s.fail}>{pass ? "PASSED" : "FAILED"}</span>
                                  : <span className={s.pending}>Pending</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function ScoreChip({ score }) {
  if (score == null) return <span className={s.noScore}>—</span>;
  const n    = Number(score);
  const pass = n >= 75;
  return (
    <span className={`${s.scoreChip} ${pass ? s.scorePass : s.scoreFail}`}>
      {n % 1 === 0 ? n : n.toFixed(2)}
    </span>
  );
}
